import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Product } from '../types';

// ============================================
// Recently Viewed Context
// ============================================
interface RecentlyViewedContextValue {
  recentProducts: Product[];
  addViewed: (product: Product) => void;
  clearRecent: () => void;
}

const RecentlyViewedContext = createContext<RecentlyViewedContextValue | null>(null);

export function RecentlyViewedProvider({ children }: { children: ReactNode }) {
  const [recentProducts, setRecentProducts] = useState<Product[]>(() => {
    try { const saved = localStorage.getItem('dtk_recent'); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });

  useEffect(() => { localStorage.setItem('dtk_recent', JSON.stringify(recentProducts)); }, [recentProducts]);

  const addViewed = useCallback((product: Product) => {
    setRecentProducts(prev => {
      const filtered = prev.filter(p => p.sku !== product.sku);
      return [product, ...filtered].slice(0, 12);
    });
  }, []);

  const clearRecent = useCallback(() => setRecentProducts([]), []);

  return (
    <RecentlyViewedContext.Provider value={{ recentProducts, addViewed, clearRecent }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export function useRecentlyViewed() {
  const ctx = useContext(RecentlyViewedContext);
  if (!ctx) throw new Error('useRecentlyViewed must be used within RecentlyViewedProvider');
  return ctx;
}

// ============================================
// Compare Context
// ============================================
interface CompareContextValue {
  compareItems: Product[];
  addToCompare: (product: Product) => void;
  removeFromCompare: (sku: string) => void;
  clearCompare: () => void;
  isInCompare: (sku: string) => boolean;
  isCompareOpen: boolean;
  setCompareOpen: (open: boolean) => void;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareItems, setCompareItems] = useState<Product[]>([]);
  const [isCompareOpen, setCompareOpen] = useState(false);

  const addToCompare = useCallback((product: Product) => {
    setCompareItems(prev => {
      if (prev.length >= 3 || prev.some(p => p.sku === product.sku)) return prev;
      return [...prev, product];
    });
  }, []);

  const removeFromCompare = useCallback((sku: string) => {
    setCompareItems(prev => prev.filter(p => p.sku !== sku));
  }, []);

  const clearCompare = useCallback(() => { setCompareItems([]); setCompareOpen(false); }, []);

  const isInCompare = useCallback((sku: string) => compareItems.some(p => p.sku === sku), [compareItems]);

  return (
    <CompareContext.Provider value={{ compareItems, addToCompare, removeFromCompare, clearCompare, isInCompare, isCompareOpen, setCompareOpen }}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
}
